import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { SxProps, DialogActions, Stack } from '@mui/material';
import { vars } from '../../../../Theme';
import CtaButton from './CtaButton';
import { useDispatch, useSelector } from 'react-redux';
import {
    AdditionalInputTypeEnum,
    onWorkflowTaskReply,
} from '../../../../redux/project/sliceProjectWorkflow';
import { IAppReduxStore } from '../../../../redux/store';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { GlobalVariables } from '../../../../GlobalVariables';
import { onDynamicActionTaskReply } from '../../../../redux/project/documents/sliceProjectDocumentsDynamicSelection';
import { IAdditionalInputTypeEnum } from './AdditionalInputContent';

const dialogActionsSx: SxProps = {
    px: 3,
    py: 1,
    borderTop: `1px solid ${vars.lightGray}`,
    justifyContent: 'end',
};

export default ({
    additionalInputState,
    setAdditionalInputErrorState,
    type,
    onConfirmClick,
}: IAdditionalInputFooterActionsProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'workflowAdditionalInput' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    const { replicaId } = useParams();

    const isLoading = useSelector((_store: IAppReduxStore) =>
        type === IAdditionalInputTypeEnum.Workflow
            ? _store.projectWorkflow.loading
            : _store.projectDocumentsDynamicSelection.loading,
    );

    const { dialogButtonYes, dialogButtonNo, dialogButtonOk, dialogButtonCancel, dialogType } =
        useSelector((_store: IAppReduxStore) =>
            type === IAdditionalInputTypeEnum.Workflow
                ? _store.projectWorkflow.additionalInput?.response
                : _store.projectDocumentsDynamicSelection.additionalInput.response,
        );

    const validate = {
        [AdditionalInputTypeEnum.ChoiceExclusive]: () => {
            if (additionalInputState.length === 0) {
                setAdditionalInputErrorState(true);

                return false;
            }

            return true;
        },
        [AdditionalInputTypeEnum.ChoiceMultiple]: () => {
            if (additionalInputState.length === 0) {
                setAdditionalInputErrorState(true);

                return false;
            }

            return true;
        },
        [AdditionalInputTypeEnum.InputDate]: () => {
            const isValidDate = dayjs(additionalInputState[0], 'DD.MM.YYYY').isValid();
            const hasValidInput = additionalInputState.length > 0 && isValidDate;

            setAdditionalInputErrorState(!hasValidInput);

            return hasValidInput;
        },
        [AdditionalInputTypeEnum.InputText]: () => {
            const inputValue = additionalInputState[0]?.trim();
            const isValid = inputValue !== undefined && inputValue !== '';

            setAdditionalInputErrorState(!isValid);

            return isValid;
        },
        [AdditionalInputTypeEnum.ShowText]: () => {
            return true;
        },
        [AdditionalInputTypeEnum.ShowUrlList]: () => {
            return true;
        },
        [AdditionalInputTypeEnum.UploadFiles]: () => {
            const hasFiles = GlobalVariables.getAllFiles().length > 0;

            setAdditionalInputErrorState(!hasFiles);

            return hasFiles;
        },
        [AdditionalInputTypeEnum.SelectViewCategoryList]: () => {
            if (additionalInputState.length === 0) {
                setAdditionalInputErrorState(true);

                return false;
            }

            return true;
        },
        [AdditionalInputTypeEnum.InputPassword]: () => {
            const inputValue = additionalInputState[0]?.trim();
            const isValid = inputValue !== undefined && inputValue !== '';

            setAdditionalInputErrorState(!isValid);

            return isValid;
        },
        [AdditionalInputTypeEnum.DigiSealSign]: () => {
            return true;
        },
    };

    const onClickConfig = reply => ({
        [IAdditionalInfoResultButtonEnum.Cancel]: () => {
            dispatch(
                reply({
                    dialogResultButton: IAdditionalInfoResultButtonEnum.Cancel,
                    additionalInputState: [],
                    replicaId: replicaId,
                }),
            );
        },
        [IAdditionalInfoResultButtonEnum.No]: () => {
            dispatch(
                reply({
                    dialogResultButton: IAdditionalInfoResultButtonEnum.No,
                    additionalInputState: [],
                    replicaId: replicaId,
                }),
            );
        },
        [IAdditionalInfoResultButtonEnum.Yes]: () => {
            if (validate[dialogType]()) {
                onConfirmClick?.();
                dispatch(
                    reply({
                        dialogResultButton: IAdditionalInfoResultButtonEnum.Yes,
                        additionalInputState: additionalInputState,
                        replicaId: replicaId,
                    }),
                );
            }
        },
        [IAdditionalInfoResultButtonEnum.Ok]: () => {
            if (validate[dialogType]()) {
                onConfirmClick?.();
                dispatch(
                    reply({
                        dialogResultButton: IAdditionalInfoResultButtonEnum.Ok,
                        additionalInputState: additionalInputState,
                        replicaId: replicaId,
                    }),
                );
            }
        },
    });

    const replyFunction =
        type === IAdditionalInputTypeEnum.Workflow ? onWorkflowTaskReply : onDynamicActionTaskReply;
    const onClickConfigInstance = onClickConfig(replyFunction);

    return (
        <DialogActions sx={dialogActionsSx}>
            <Stack direction='row' sx={{ gap: 0.5 }}>
                {dialogButtonCancel && (
                    <CtaButton
                        variant='text'
                        text={t('cancel')}
                        onClick={() =>
                            onClickConfigInstance[IAdditionalInfoResultButtonEnum.Cancel]()
                        }
                        disabled={isLoading}
                    />
                )}

                {dialogButtonNo && (
                    <CtaButton
                        variant='text'
                        text={t('no')}
                        onClick={() => onClickConfigInstance[IAdditionalInfoResultButtonEnum.No]()}
                        disabled={isLoading}
                    />
                )}

                {dialogButtonYes && (
                    <CtaButton
                        variant='contained'
                        text={t('yes')}
                        onClick={() => onClickConfigInstance[IAdditionalInfoResultButtonEnum.Yes]()}
                        disabled={isLoading}
                    />
                )}
                {dialogButtonOk && (
                    <CtaButton
                        variant='contained'
                        text={t('ok')}
                        onClick={() => onClickConfigInstance[IAdditionalInfoResultButtonEnum.Ok]()}
                        disabled={isLoading}
                    />
                )}
            </Stack>
        </DialogActions>
    );
};

export enum IAdditionalInfoResultButtonEnum {
    Yes = 'yes',
    No = 'no',
    Ok = 'ok',
    Cancel = 'cancel',
}

interface IAdditionalInputFooterActionsProps {
    additionalInputState: string[];
    setAdditionalInputErrorState: Dispatch<React.SetStateAction<boolean>>;
    type: IAdditionalInputTypeEnum;
    onConfirmClick?: () => void;
}
